<template>
  <div v-if="values" class="h-full">
    <BaseTable
      table-id="list-rows"
      :headers="headers"
      :list="values"
      clickable
      hide-grouping
      :is-add-new-hidden="mode == 'view' || !hasListValues"
      @refresh-data="onRefreshData"
      @table-item-clicked="onListRowClick"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onListRowClick">
      <template #custom="{ item, header }">
        <div class="flex items-center justify-center">
          <div v-if="header === 'image'">
            <button
              v-if="item.image"
              type="button"
              class="rounded"
              @click.stop="onImageExpandClick(item.image)">
              <IconImage class="w-5 h-5 rounded text-theme-600 hover:bg-cyan-light" />
            </button>
            <span v-else> - </span>
          </div>
        </div>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1" v-if="props.mode == 'edit'">
          <button
            v-if="item.isFromManager"
            type="button"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            @click="onDeleteRow(item as QstListRow)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>
    <InfoModal v-if="previewImage" @primary-button-clicked="previewImage = null">
      <template #content> <img :src="previewImage" /> </template>
    </InfoModal>
    <QuestionListValuesModal
      v-if="listRow"
      :is-open="isAddEditModalOpen"
      :mode="modalMode"
      :item="listRow"
      @save="onListValueSave"
      @close="isAddEditModalOpen = false"></QuestionListValuesModal>
    <BaseModalConfirm
      v-if="deleteRowItem && modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { 'item': deleteRowItem?.description }) }}
      </template>
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import type { QstListRow } from '@/types/Questionnaires';
import BaseTable from '../common/BaseTable.vue';
import InfoModal from '../common/InfoModal.vue';
import IconImage from '@/assets/icons/IconImage.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import QuestionListValuesModal from './QuestionListValuesModal.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import { type TableFilterData } from '@/types/Base';
import { filterData, sortData } from '@/utils/table';
import { v4 as uuidv4 } from 'uuid';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  mode: 'view' | 'edit';
  listRows: QstListRow[];
  hasListValues: boolean;
  listId?: string;
}>();

const values = defineModel<QstListRow[]>({ default: [] });

const emit = defineEmits(['delete']);

onMounted(() => {
  if (values.value) values.value = deepCopy<QstListRow[]>(values.value);
});

const previewImage = ref<string | null>(null);

function onImageExpandClick(image: string) {
  previewImage.value = `data:image/jpeg;base64,${image}`;
}

watch(
  () => props.mode,
  (newMode) => {
    const actionsColumn = headers.value.find((header) => header.value === 'actions');
    if (actionsColumn) {
      actionsColumn.active = newMode === 'edit';
    }
  }
);

const { t: $t } = useI18n({ useScope: 'global' });

const headers = ref([
  { value: 'description', text: $t('questionnaire.description'), type: 'text', active: true },
  { value: 'sort', text: $t('question.sort'), type: 'text', active: true },
  { value: 'image', text: $t('question.image'), type: 'custom', active: true, isSortHidden: true },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
]);

function onRefreshData(data: TableFilterData) {
  values.value = props.listRows;
  if (data && data.query)
    values.value = filterData(values.value, data.query, ['description', 'sort']);
  if (data && data.column !== undefined && data.order)
    values.value = sortData(values.value, data.column, data.order, headers.value);
}

const isAddEditModalOpen = ref(false);
const modalMode = ref<'view' | 'edit'>('view');

const listId = ref<string>(props.listId ? props.listId : uuidv4());

const listRow = ref<QstListRow>();
function onListRowClick(item?: QstListRow) {
  if (item) {
    listRow.value = item;
    listRow.value.isFromManager && props.mode == 'edit'
      ? (modalMode.value = 'edit')
      : (modalMode.value = 'view');
  } else {
    listRow.value = {
      id: uuidv4(),
      qstListId: listId.value,
      sort: 0,
      description: '',
      isFromManager: true,
      isActive: true,
    };
    modalMode.value = 'edit';
  }
  isAddEditModalOpen.value = true;
}

function onListValueSave(item: QstListRow) {
  item.modified = true;

  const index = values.value.findIndex((row) => row.id === item.id);
  if (index !== -1) values.value[index] = item;
  else values.value.push(item);
  isAddEditModalOpen.value = false;
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<QstListRow>();

function onDeleteRow(item: QstListRow) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

const deletedIds = ref<string[]>([]);

function onDeleteMultiselect(items: QstListRow[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  deletedIds.value.concat(idsToDelete);
  values.value = values.value.filter((x) => !x.id || !idsToDelete.includes(x.id));

  emit(
    'delete',
    items.filter((item) => item.createdDate && item.isFromManager).map((item) => item.id)
  );
}

function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  values.value = values.value.filter((x) => x.id !== idToDelete);
  deletedIds.value.push(deleteRowItem.value.id);
  if (deleteRowItem.value.createdDate) emit('delete', [deleteRowItem.value.id]);
}
</script>
